import { useEffect, useMemo, useRef, useState } from 'react';

import { useEffectOnce } from 'react-use';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import { IModalStateInvite, InputAction } from '~/shared';
import {
	StyledButtonContent,
	StyledButtonsContainer,
	StyledContainer,
	StyledKeyContainer,
	StyledProgress,
	StyledProgressWrapper,
} from './ModalInvite.styled';

const ModalInvite: React.FC<{ state: IModalStateInvite }> = ({ state }) => {
	const [savedText, setSavedText] = useState<string | undefined>(state?.title);
	const actionButton = useObservable(rage.buttonActions.getDisplayedAction());
	const notification = useObservable(
		rage.notifications.getDisplayedNotification(),
	);
	const notifications = useObservable(
		rage.notifications.getDisplayedNotifications(),
	);
	const playerIsTalking = useObservable(rage.player.getPlayerIsTalking());
	const progressRef = useRef<HTMLDivElement | null>(null);

	const notificationCount = useMemo(() => {
		if (notification?.duration !== undefined) {
			return 1;
		}

		return Math.min(3, notifications.length);
	}, [notification, notifications]);

	useEffect(() => {
		if (state?.title) {
			setSavedText(state.title);
		}
	}, [state?.title]);

	useInputAction(InputAction.InviteAccept, () => {
		if (state) {
			rage.modal.replyToCurrentModal({ isAccepted: true });
		}
	});

	useInputAction(InputAction.InviteDecline, () => {
		if (state) {
			rage.modal.replyToCurrentModal({ isAccepted: false });
		}
	});

	useEffectOnce(() => {
		const element = progressRef.current;
		if (!element) {
			return;
		}

		let frameId: number;
		const startedAt = performance.now();
		let hasExpired = false;

		const updateProgress = (timestamp: number) => {
			const elapsed = Math.min(
				timestamp - startedAt,
				state?.inviteExpirationTime || 0,
			);
			const progress = 1 - elapsed / (state?.inviteExpirationTime || 0);

			element.style.transform = `scaleX(${progress})`;

			if (elapsed >= (state?.inviteExpirationTime || 0) && !hasExpired) {
				hasExpired = true;
				if (state) {
					rage.modal.replyToCurrentModal({ isAccepted: false });
				}
			}

			if (elapsed < (state?.inviteExpirationTime || 0)) {
				frameId = requestAnimationFrame(updateProgress);
			}
		};

		element.style.transform = 'scaleX(1)';
		frameId = requestAnimationFrame(updateProgress);

		return () => {
			cancelAnimationFrame(frameId);
		};
	});

	const bottom = useMemo(() => {
		const notificationLiftByCount = [0, 60, 110, 160];
		if (actionButton && playerIsTalking) {
			return 170 + notificationLiftByCount[notificationCount];
		}

		if (playerIsTalking) {
			return 110 + notificationLiftByCount[notificationCount];
		}

		if (actionButton) {
			return 100 + notificationLiftByCount[notificationCount];
		}

		return 40 + notificationLiftByCount[notificationCount];
	}, [actionButton, playerIsTalking, notificationCount]);

	const displayText = state?.title || savedText;

	return (
		<StyledContainer style={{ bottom: bottom }}>
			<Text style={{ fontSize: 14, fontWeight: 500, color: '#FFFFFF' }}>
				{displayText}
			</Text>
			<StyledButtonsContainer>
				<Button
					color={ButtonColors.Buy}
					height={ButtonHeight.Height30}
					borderRadius={ButtonBorderRadius.Radius5}
				>
					<StyledButtonContent style={{ gap: 9 }}>
						<StyledKeyContainer>
							<Text>У</Text>
						</StyledKeyContainer>
						<Text>Согласиться</Text>
					</StyledButtonContent>
				</Button>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height30}
					borderRadius={ButtonBorderRadius.Radius5}
				>
					<StyledButtonContent style={{ gap: 13 }}>
						<StyledKeyContainer>
							<Text>N</Text>
						</StyledKeyContainer>
						<Text>Отклонить</Text>
					</StyledButtonContent>
				</Button>
			</StyledButtonsContainer>
			<StyledProgressWrapper>
				<StyledProgress ref={progressRef} />
			</StyledProgressWrapper>
		</StyledContainer>
	);
};

export { ModalInvite };
